// 🔒 LOCKED: Extras & Credits system
// Do not modify structure, UI, or logic without review

import { supabase } from '../lib/supabase';
import { signedLinesAmount } from '../utils/extraSign';

function toCamelCase(obj) {
  if (!obj) return obj;
  const camelObj = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = obj?.[key];
  }
  return camelObj;
}

function toSnakeCase(obj) {
  if (!obj) return obj;
  const snakeObj = {};
  for (const key in obj) {
    const snakeKey = key?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
    snakeObj[snakeKey] = obj?.[key];
  }
  return snakeObj;
}

// ─── Ordre de changement (ODC) du client ─────────────────────────────────────
// Un extra/credit APPROUVE et CONTRACTUEL attend l'ODC du client (sans ODC, pas
// de facturation). Les categories internes (retrofacturation, erreur interne,
// T&M absorbe, litige) n'en ont pas besoin : « Non requis ».
// Une categorie vide (anciennes lignes) est traitee comme contractuelle.
export const isCoRequired = (category) => !category || category === 'contractual';

// Regle unique, appliquee a CHAQUE ecriture de la ligne, dans le meme UPDATE :
//   - approuve + ODC requis : co_status -> 'pending' s'il est vide (un ODC deja
//     recu n'est jamais ecrase) ;
//   - sinon (autre statut, ou categorie interne) : co_status / co_received_at /
//     co_received_by -> NULL.
// co_number n'a rien a voir : c'est le n° de directive texte libre.
export const coFieldsFor = ({ status, category, coStatus }) => {
  if (status === 'approved' && isCoRequired(category)) {
    return coStatus ? {} : { co_status: 'pending' };
  }
  return { co_status: null, co_received_at: null, co_received_by: null };
};

// Etat actuel de la ligne, pour appliquer la regle quand l'ecriture ne porte
// pas elle-meme le statut ou la categorie.
const readCoState = async (id) => {
  const { data, error } = await supabase
    ?.from('project_extras')
    ?.select('status, category, co_status')
    ?.eq('id', id)
    ?.single();
  if (error) throw error;
  return data || {};
};

export const projectExtrasService = {
  async getByProjectId(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('project_extras')
        ?.select('*')
        ?.eq('project_id', projectId)
        ?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching project extras:', error?.message);
        return [];
      }

      const extras = data?.map(toCamelCase) || [];

      // Compute hasSignedAvenant per extra: one query for all extra IDs
      if (extras?.length > 0) {
        const extraIds = extras?.map(e => e?.id);
        const { data: signedCOs } = await supabase
          ?.from('subcontract_change_orders')
          ?.select('source_extra_id')
          ?.in('source_extra_id', extraIds)
          ?.in('status', ['sent', 'signed', 'approved']);

        const signedSet = new Set((signedCOs || []).map(co => co.source_extra_id));
        return extras?.map(e => ({ ...e, hasSignedAvenant: signedSet?.has(e?.id) }));
      }

      return extras?.map(e => ({ ...e, hasSignedAvenant: false }));
    } catch (err) {
      console.error('Error fetching project extras:', err);
      return [];
    }
  },

  async create(extraData) {
    try {
      const snakeData = toSnakeCase(extraData);
      // Ensure profit is included (defaults to 0 if not provided)
      if (snakeData?.profit === undefined || snakeData?.profit === null) {
        snakeData.profit = 0;
      }
      // Include category if provided (DB value only — never the human-readable label)
      if (snakeData?.category === undefined) {
        snakeData.category = null;
      }
      // ODC : un extra cree directement approuve (et contractuel) attend son ODC.
      Object.assign(snakeData, coFieldsFor({ status: snakeData?.status, category: snakeData?.category, coStatus: null }));
      const { data, error } = await supabase
        ?.from('project_extras')
        ?.insert([snakeData])
        ?.select()
        ?.single();

      if (error) {
        console.error('Error creating project extra:', error?.message);
        throw error;
      }

      return data ? toCamelCase(data) : null;
    } catch (err) {
      console.error('Error creating project extra:', err);
      throw err;
    }
  },

  async update(id, updateData) {
    try {
      const snakeData = toSnakeCase(updateData);
      // Ensure profit is included when provided
      if ('profit' in snakeData && (snakeData?.profit === undefined || snakeData?.profit === null)) {
        snakeData.profit = 0;
      }
      // ODC : la regle s'applique a toute ecriture (meme UPDATE). Statut et
      // categorie : ceux de l'ecriture, sinon ceux de la ligne.
      const current = await readCoState(id);
      Object.assign(snakeData, coFieldsFor({
        status: 'status' in snakeData ? snakeData?.status : current?.status,
        category: 'category' in snakeData ? snakeData?.category : current?.category,
        coStatus: current?.co_status,
      }));
      const { data, error } = await supabase
        ?.from('project_extras')
        ?.update(snakeData)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        console.error('Error updating project extra:', error?.message);
        throw error;
      }

      return data ? toCamelCase(data) : null;
    } catch (err) {
      console.error('Error updating project extra:', err);
      throw err;
    }
  },

  async updateStatus(id, status) {
    try {
      const updatePayload = { status };
      if (status === 'approved') {
        updatePayload.approved_at = new Date()?.toISOString();
      }
      // ODC : meme UPDATE. La ligne est relue pour sa categorie, et pour ne
      // jamais ecraser un ODC deja recu.
      const current = await readCoState(id);
      Object.assign(updatePayload, coFieldsFor({ status, category: current?.category, coStatus: current?.co_status }));

      const { data, error } = await supabase
        ?.from('project_extras')
        ?.update(updatePayload)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        console.error('Error updating extra status:', error?.message);
        throw error;
      }

      const updated = data ? toCamelCase(data) : null;
      return updated;
    } catch (err) {
      console.error('Error updating extra status:', err);
      throw err;
    }
  },

  /**
   * Create (or update) one avenant per subcontractor for the given extra.
   * selections = [{ subcontractId, lineIds: [...], notes }]
   * Only groups with ≥1 checked line are processed.
   * Idempotency: if an avenant already exists for (source_extra_id, subcontract_id),
   * UPDATE its amount/description/notes instead of inserting a duplicate.
   */
  async createAvenantsForExtra(extraId, selections) {
    if (!selections || selections?.length === 0) return;

    // Fetch the extra header for title + project_id
    const { data: extraData, error: extraErr } = await supabase
      ?.from('project_extras')
      ?.select('id, title, type, project_id')
      ?.eq('id', extraId)
      ?.single();

    if (extraErr || !extraData) {
      console.error('createAvenantsForExtra: could not fetch extra', extraErr?.message);
      throw extraErr || new Error('Extra not found');
    }

    // Fetch all lines for this extra (to resolve amounts + descriptions by id)
    const { data: allLines, error: linesErr } = await supabase
      ?.from('project_extra_lines')
      ?.select('id, subcontract_id, amount, description')
      ?.eq('extra_id', extraId);

    if (linesErr) {
      console.error('createAvenantsForExtra: could not fetch lines', linesErr?.message);
      throw linesErr;
    }

    const lineMap = {};
    for (const l of allLines || []) {
      lineMap[l.id] = l;
    }

    for (const sel of selections) {
      if (!sel?.lineIds || sel?.lineIds?.length === 0) continue;

      const selectedLines = sel?.lineIds?.map(lid => lineMap?.[lid])?.filter(Boolean);

      if (selectedLines?.length === 0) continue;

      // Lignes stockees relatives au type (voir utils/extraSign.js), et parfois
      // negatives (retrait dans un extra) : la somme garde son signe. Un credit
      // donne un avenant NEGATIF ; un sous-traitant a total negatif dans un
      // extra aussi.
      const totalAmount = signedLinesAmount(
        extraData,
        selectedLines?.reduce((sum, l) => sum + (Number(l?.amount) || 0), 0)
      );

      const descriptions = selectedLines?.map(l => l?.description?.trim())?.filter(Boolean);

      const combinedDescription =
        descriptions?.length > 0
          ? `${extraData?.title} — ${descriptions?.join(', ')}`
          : extraData?.title || null;

      // Idempotency: check if avenant already exists for this (extra, subcontract)
      const { data: existing } = await supabase
        ?.from('subcontract_change_orders')
        ?.select('id, co_number, status')
        ?.eq('source_extra_id', extraId)
        ?.eq('subcontract_id', sel?.subcontractId)
        ?.maybeSingle();

      if (existing) {
        // Un avenant ENVOYE ou SIGNE engage deja le sous-traitant : on ne le
        // reecrit jamais, ni son montant, ni sa description. Seuls les
        // brouillons sont mis a jour.
        if (existing?.status === 'sent' || existing?.status === 'signed') {
          console.warn(
            'createAvenantsForExtra: avenant', existing?.status, 'laisse intact',
            existing?.co_number || existing?.id,
            '— extra', extraId
          );
          continue;
        }
        // UPDATE existing avenant
        const { error: updateErr } = await supabase
          ?.from('subcontract_change_orders')
          ?.update({
            amount: totalAmount,
            description: combinedDescription,
            notes: sel?.notes || null,
          })
          ?.eq('id', existing?.id);

        if (updateErr) {
          console.error('createAvenantsForExtra: update error', updateErr?.message);
        }
      } else {
        // Generate co_number using the canonical {cost_code}.AV{n} pattern
        const { data: subcontractData } = await supabase
          ?.from('subcontracts')
          ?.select('id, project_costs(cost_code)')
          ?.eq('id', sel?.subcontractId)
          ?.single();

        const costCode = subcontractData?.project_costs?.cost_code || 'CO';

        const { data: existingCOs } = await supabase
          ?.from('subcontract_change_orders')
          ?.select('co_number')
          ?.eq('subcontract_id', sel?.subcontractId);

        const maxAv = (existingCOs || [])?.reduce((max, co) => {
          const match = co?.co_number?.match(/\.AV(\d+)$/i);
          const num = match ? parseInt(match?.[1], 10) : 0;
          return num > max ? num : max;
        }, 0);

        const coNumber = `${costCode}.AV${maxAv + 1}`;
        const now = new Date()?.toISOString();

        const { error: insertErr } = await supabase
          ?.from('subcontract_change_orders')
          ?.insert({
            project_id: extraData?.project_id,
            subcontract_id: sel?.subcontractId,
            source_extra_id: extraId,
            co_number: coNumber,
            description: combinedDescription,
            amount: totalAmount,
            notes: sel?.notes || null,
            status: 'draft',
            created_at: now,
            approved_at: null,
          });

        if (insertErr) {
          console.error('createAvenantsForExtra: insert error', insertErr?.message);
        }
      }
    }
  },

  /**
   * ODC recu : a appeler UNIQUEMENT apres un televersement reussi du document.
   * Garde : seulement un extra approuve dont l'ODC est en attente.
   */
  async markCoReceived(id) {
    // Categorie interne : pas d'ODC (un seul filtre .or() par requete, d'ou la
    // verification ici plutot que dans l'UPDATE).
    if (!isCoRequired((await readCoState(id))?.category)) throw new Error('CO_NOT_REQUIRED');
    const { data: { user } = {} } = (await supabase?.auth?.getUser()) || {};
    const { data, error } = await supabase
      ?.from('project_extras')
      ?.update({
        co_status: 'received',
        co_received_at: new Date()?.toISOString(),
        co_received_by: user?.id || null,
      })
      ?.eq('id', id)
      ?.eq('status', 'approved')
      ?.or('co_status.is.null,co_status.eq.pending')
      ?.select('id')
      ?.maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('CO_NOT_PENDING');
    return true;
  },

  /** ODC remis en attente. Le document reste dans les pieces. */
  async resetCoPending(id) {
    if (!isCoRequired((await readCoState(id))?.category)) throw new Error('CO_NOT_REQUIRED');
    const { data, error } = await supabase
      ?.from('project_extras')
      ?.update({ co_status: 'pending', co_received_at: null, co_received_by: null })
      ?.eq('id', id)
      ?.eq('status', 'approved')
      ?.select('id')
      ?.maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('CO_NOT_APPROVED');
    return true;
  },

  async delete(id) {
    try {
      const { error } = await supabase
        ?.from('project_extras')
        ?.delete()
        ?.eq('id', id);

      if (error) {
        console.error('Error deleting project extra:', error?.message);
        throw error;
      }

      return true;
    } catch (err) {
      console.error('Error deleting project extra:', err);
      throw err;
    }
  },

  calculateKPIs(extras) {
    const approvedExtras = extras?.filter(e => e?.type === 'extra' && e?.status === 'approved') || [];
    const approvedCredits = extras?.filter(e => e?.type === 'credit' && e?.status === 'approved') || [];

    const getRealValue = (e) => parseFloat(e?.costAccepted ?? e?.amount) || 0;

    const totalApprovedExtras = approvedExtras?.reduce((sum, e) => sum + getRealValue(e), 0);
    const totalApprovedCredits = approvedCredits?.reduce((sum, e) => sum + getRealValue(e), 0);
    const netChange = totalApprovedExtras - totalApprovedCredits;

    return {
      totalApprovedExtras,
      totalApprovedCredits,
      netChange
    };
  }
};

export default projectExtrasService;
